import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CalendarClock, CalendarDays, CheckCircle2, CreditCard, Loader2, Minus, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { VISIT_MODE_OPTIONS, type VisitMode } from '@/features/vendor-service/types';
import { Input } from '@/components/ui/input';
import { SelectField } from '@/components/ui/select';
import { ErrorState } from '@/components/ui/error-state';
import { DatePicker } from '@/components/ui/date-picker';
import { form_error_message } from '@/lib/forms';
import { ApiError } from '@/lib/api';
import { RegionBlockCallout, as_region_block } from '@/components/region-block-callout';
import { use_vendor_detail } from '@/features/discovery/hooks';
import { ProviderAvatar, is_at_your_door } from '@/components/provider-avatar';
import { use_pets } from '@/features/pet/hooks';
import { use_slots, use_create_booking, use_pay_booking, use_verify_payment } from '@/features/booking/hooks';
import { use_offering_availability } from '@/features/dining/hooks';
import { open_razorpay_checkout } from '@/features/booking/razorpay-checkout';
import type { Booking, Checkout } from '@/features/booking/types';
import type { DiscoveryService } from '@/features/discovery/types';
import type { PublicOffering } from '@/features/dining/types';
import { MODE_LABEL } from '@/features/dining/types';
import { SPECIES_OPTIONS, type PetSpecies } from '@/features/pet/types';
import { use_session_store } from '@/lib/session-store';
import { ImageCarousel } from '@/components/image-carousel';
import { SavedAddressPicker } from '@/components/saved-address-picker';

/** Coarse time-of-day options for a preferred cremation pickup window. */
const PICKUP_TIME_OPTIONS = [
  { value: '09:00', label: 'Morning (around 9 AM)' },
  { value: '12:00', label: 'Midday (around 12 PM)' },
  { value: '15:00', label: 'Afternoon (around 3 PM)' },
  { value: '18:00', label: 'Evening (around 6 PM)' },
];

const IST = 'Asia/Kolkata';
const today_ist = () => new Date().toLocaleDateString('en-CA', { timeZone: IST }); // YYYY-MM-DD

function slot_label(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: '2-digit', minute: '2-digit' });
}
function event_label(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { timeZone: IST, dateStyle: 'medium', timeStyle: 'short' });
}

type NavState = { service?: DiscoveryService; offering?: PublicOffering; vendor_name?: string; vendor_id?: string };
type Step = 'select' | 'pay' | 'done';

export function BookPage() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const state = (useLocation().state ?? {}) as NavState;
  const service = state.service;
  const offering = state.offering;
  const is_dining = !!offering;
  const is_event = offering?.mode === 'EVENT';
  const is_walk = service?.module_type === 'WALKER';
  const is_trainer = service?.module_type === 'TRAINER';
  const is_cremation = service?.module_type === 'CREMATION';
  const is_lab = service?.module_type === 'LAB';
  /*
   * Client change (9 Oct 2026): a lab test is On site or Home visit; a cremation is Pickup,
   * Home visit or In location. The service lists what it offers; nothing listed keeps the old
   * behaviour (lab home visit, cremation pickup). Only the modes that come to the customer
   * need their address.
   */
  // Grooming is at the salon unless the groomer offers home visit / pickup-drop (Nov 2026).
  const is_groomer = service?.module_type === 'GROOMER';
  const offered_modes: VisitMode[] =
    is_lab || is_cremation || is_groomer
      ? service?.visit_modes?.length
        ? service.visit_modes
        : [is_lab ? 'HOME_VISIT' : is_groomer ? 'AT_CENTER' : 'PICKUP']
      : [];
  const [visit_mode, set_visit_mode] = useState<VisitMode | undefined>(undefined);
  const chosen_mode = visit_mode ?? offered_modes[0];
  const visits_customer = chosen_mode === undefined || chosen_mode === 'PICKUP' || chosen_mode === 'HOME_VISIT';
  // Vet/clinic visits are at-premises like grooming, but the pet is the patient: the
  // clinical record is keyed to it, and a booking made without one strands the vet
  // (they cannot file the visit record afterwards).
  const is_clinical = service?.module_type === 'VET' || service?.module_type === 'CLINIC';
  // WALKER + home-visit TRAINER + LAB home collection all need a pet + a visit address
  // (shared pickup_* fields) and a bookable slot.
  const needs_visit = is_walk || is_trainer || (is_lab && visits_customer);
  // A specific pet is REQUIRED for visit bookings, grooming, and vet/clinic consultations.
  const needs_pet = needs_visit || is_lab || is_groomer || is_clinical;
  // Cremation is an on-demand home-pickup request (no slot grid) that also uses pickup_* fields.
  // Home / pickup grooming also needs the address (Nov 2026).
  const needs_pickup = needs_visit || ((is_cremation || is_groomer) && visits_customer);
  const visit_noun = is_walk
    ? 'walk'
    : is_lab
      ? 'sample collection'
      : is_groomer || is_clinical
        ? 'appointment'
        : 'session';
  const pet_noun = is_walk ? 'dog' : 'pet';

  const pets_q = use_pets();
  /*
   * The provider's face, for the at-your-door modules.
   *
   * Fetched rather than threaded through router state: state is written by whichever screen
   * navigated here, so a shared link or an older build would arrive without the field and
   * silently show no photo on the one screen where the customer most needs to know who is
   * coming. In the normal path the customer just came from this vendor's page, so the query
   * is already cached and this costs nothing.
   */
  const vendor_q = use_vendor_detail(state.vendor_id);
  const create_m = use_create_booking();

  const [date, set_date] = useState(today_ist());
  // Events don't take a date — the occurrence is fixed; tables/passes/services do.
  // Cremation is on-demand (no bookable slots), so it never queries availability.
  const skip_slots = is_dining || is_cremation;
  const service_slots_q = use_slots(skip_slots ? undefined : serviceId, skip_slots ? undefined : date);
  const offering_avail_q = use_offering_availability(serviceId, date, is_dining);
  const active_q = is_dining ? offering_avail_q : service_slots_q;
  const slots = active_q.data?.slots ?? [];
  // How far ahead this provider takes bookings (service slots only; dining has no horizon).
  const horizon_days = service_slots_q.data?.horizon_days;
  // The latest date that still has any bookable time (horizon is a rolling window from now).
  const max_bookable_date =
    horizon_days != null
      ? new Date(Date.now() + horizon_days * 86_400_000).toLocaleDateString('en-CA', { timeZone: IST })
      : null;
  const date_beyond_horizon = max_bookable_date != null && date > max_bookable_date;

  const [slot_start, set_slot_start] = useState<string | null>(null);
  const [pet_id, set_pet_id] = useState<string>('');
  const [party_size, set_party_size] = useState(offering?.min_party ?? 1);
  const [notes, set_notes] = useState('');
  const [error, set_error] = useState<string | null>(null);
  /* Kept apart from `error`: a region block is a different kind of answer — nothing to
     retry and nothing the customer got wrong — so it gets its own presentation. */
  const [region_block, set_region_block] = useState<ApiError | null>(null);

  // Walker / cremation pickup details (shared pickup_* fields).
  const [saved_address_id, set_saved_address_id] = useState<string | undefined>(undefined);
  const [pickup_address, set_pickup_address] = useState('');
  const [pickup_landmark, set_pickup_landmark] = useState('');
  const [pickup_pincode, set_pickup_pincode] = useState('');
  const [contact_phone, set_contact_phone] = useState('');
  const [access_notes, set_access_notes] = useState('');

  // Cremation on-demand request: deceased-pet snapshot + a preferred pickup time-of-day.
  const [deceased_name, set_deceased_name] = useState('');
  const [deceased_species, set_deceased_species] = useState<PetSpecies | ''>('');
  const [deceased_weight, set_deceased_weight] = useState('');
  const [pickup_time, set_pickup_time] = useState(PICKUP_TIME_OPTIONS[0].value);

  const pay_m = use_pay_booking();
  const verify_m = use_verify_payment();
  const phone = use_session_store((s) => s.phone) ?? undefined;

  const [step, set_step] = useState<Step>('select');
  const [booking, set_booking] = useState<Booking | null>(null);
  const [checkout, set_checkout] = useState<Checkout | null>(null);

  const pets = pets_q.data ?? [];

  // Events expose a single occurrence — auto-select it so there's no empty grid.
  useEffect(() => {
    if (is_event && !slot_start && slots.length > 0) set_slot_start(slots[0].slot_start);
  }, [is_event, slot_start, slots]);

  // Prefill the visit contact phone with the signed-in number as a sensible default.
  useEffect(() => {
    if (needs_pickup && phone && !contact_phone) set_contact_phone(phone);
  }, [needs_pickup, phone, contact_phone]);

  const selected_slot = slots.find((s) => s.slot_start === slot_start) ?? null;
  const max_party = offering
    ? Math.max(offering.min_party, Math.min(offering.max_party, selected_slot?.remaining ?? offering.max_party))
    : 50;
  const unit_price = offering ? Number(offering.price_inr) : 0;
  const dining_total = offering ? (offering.price_unit === 'PER_GUEST' ? unit_price * party_size : unit_price) : 0;

  async function reserve() {
    set_error(null);
    set_region_block(null);
    // Cremation builds its "preferred pickup window" from the chosen date + time-of-day.
    const effective_slot = is_cremation ? new Date(`${date}T${pickup_time}:00`).toISOString() : slot_start;
    if (!serviceId || !effective_slot) return set_error(is_event ? 'This event is not open for booking.' : 'Pick a time first.');
    if (needs_pet && !pet_id) return set_error(`Choose which ${pet_noun} the ${visit_noun} is for.`);
    if (needs_visit) {
      if (!pickup_address.trim()) return set_error(`Enter a visit address for the ${visit_noun}.`);
      if (!contact_phone.trim()) return set_error(`Enter a contact phone for the ${visit_noun}.`);
    }
    if (is_cremation) {
      if (!deceased_name.trim()) return set_error("Please enter your pet's name.");
      if (!deceased_species) return set_error("Please choose your pet's species.");
      if (visits_customer && !pickup_address.trim()) return set_error('Enter a pickup address.');
      if (visits_customer && !contact_phone.trim()) return set_error('Enter a contact phone.');
    }
    try {
      const b = await create_m.mutateAsync({
        service_id: serviceId,
        slot_start: effective_slot,
        pet_id: pet_id || undefined,
        party_size: is_dining ? party_size : undefined,
        visit_mode: offered_modes.length > 0 ? chosen_mode : undefined,
        notes: notes.trim() || undefined,
        ...(needs_pickup
          ? {
              saved_address_id,
              pickup_address: pickup_address.trim(),
              pickup_landmark: pickup_landmark.trim() || undefined,
              pickup_pincode: pickup_pincode.trim() || undefined,
              contact_phone: contact_phone.trim(),
              access_notes: access_notes.trim() || undefined,
            }
          : {}),
        ...(is_cremation
          ? {
              deceased_pet_name: deceased_name.trim(),
              deceased_pet_species: deceased_species,
              deceased_pet_weight_kg: deceased_weight.trim() ? Number(deceased_weight) : undefined,
            }
          : {}),
      });
      set_booking(b);
      set_checkout(b.checkout);
      // Dining + paid services are prepaid (PENDING_PAYMENT). Free ₹0 services
      // come back AWAITING_VENDOR with no payment → straight to confirmation.
      set_step(b.status === 'AWAITING_VENDOR' && !b.payment ? 'done' : 'pay');
    } catch (err) {
      const blocked = as_region_block(err);
      if (blocked) set_region_block(blocked);
      else set_error(form_error_message(err, 'Could not reserve that. It may have just been taken.'));
    }
  }

  async function pay_dummy() {
    if (!booking) return;
    set_error(null);
    set_region_block(null);
    try {
      const paid = await pay_m.mutateAsync(booking.id);
      set_booking(paid);
      set_step('done');
    } catch (err) {
      set_error(form_error_message(err, 'Payment failed. Please try again.'));
    }
  }

  async function pay_razorpay() {
    if (!booking || !checkout) return;
    set_error(null);
    set_region_block(null);
    try {
      await open_razorpay_checkout({
        checkout,
        service_name: offering?.name ?? service?.name ?? 'Booking',
        contact: phone,
        on_success: async (resp) => {
          try {
            const verified = await verify_m.mutateAsync({
              id: booking.id,
              input: {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
            });
            set_booking(verified);
            set_step('done');
          } catch (err) {
            set_error(form_error_message(err, 'Payment verification failed. If you were charged, contact support.'));
          }
        },
        on_dismiss: () => set_error('Payment cancelled. You can try again.'),
        on_failure: (message) => set_error(message),
      });
    } catch (err) {
      set_error(form_error_message(err, 'Could not open the payment gateway.'));
    }
  }

  const amount = booking?.price_inr ?? (offering ? String(dining_total) : service?.price_inr);
  const paying = pay_m.isPending || verify_m.isPending;
  const title = offering?.name ?? service?.name ?? 'Book a slot';
  const price_hint = offering
    ? `₹${offering.price_inr}${offering.price_unit === 'PER_GUEST' ? ' / guest' : ''}`
    : service
      ? `₹${service.price_inr}${service.duration_minutes ? ` · ${service.duration_minutes} min` : ''}`
      : '';
  const subtitle = [state.vendor_name, offering ? MODE_LABEL[offering.mode] : null, price_hint].filter(Boolean).join(' · ');
  const header_images = offering?.images ?? service?.images ?? [];

  // The service/offering arrives purely as router state, so a refresh, a shared
  // link or a back-navigation lands here with nothing. Every module flag would
  // then read false — which silently turns a VET booking into a pet-less one and
  // drops the pickup address a walker/trainer/lab booking needs. Refuse to book
  // blind rather than take payment for something we can't describe or fulfil.
  if (!service && !offering) {
    return (
      <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
        <Button variant="ghost" size="sm" className="self-start" onClick={() => navigate('/app/discover')}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <ErrorState
          title="Let's start that booking again"
          description="We lost the details of what you were booking. Pick the service again and we'll take you straight back here."
          action={
            <Button variant="gradient" onClick={() => navigate('/app/discover')} data-testid="book-restart">
              Browse services
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => navigate(state.vendor_id ? `/app/discover/${state.vendor_id}` : '/app/discover')}
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>

      <div className="flex items-start gap-3">
        {/*
          Scoped by the SERVICE's module, not the vendor's: someone booking grooming from a
          vendor who also walks dogs is not being visited at home, and the face belongs on
          the booking it applies to.
        */}
        {is_at_your_door(service?.module_type ? [service.module_type] : []) && (
          <ProviderAvatar
            path={vendor_q.data?.profile_photo_path ?? null}
            name={state.vendor_name}
            size="md"
            data-testid="book-provider-avatar"
          />
        )}
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold">{title}</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      {header_images.length > 0 && <ImageCarousel images={header_images} cover />}

      {/* Step 1 — pick date/time + party */}
      {step === 'select' && (
        <Card>
          <CardContent className="flex flex-col gap-4 p-5">
            {is_cremation && (
              <>
                <p className="rounded-input border-2 border-brand-outline-variant/15 bg-secondary/30 p-3 text-sm text-muted-foreground" data-testid="cremation-intro">
                  We’re so sorry for your loss. Share a few details and your provider will arrange a gentle home pickup and take care of everything from here.
                </p>

                {pets.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="cremation-pet">Pick from your pets (optional)</Label>
                    <SelectField
                      id="cremation-pet"
                      value={pet_id}
                      data-testid="cremation-pet"
                      placeholder="Enter details manually"
                      onValueChange={(v) => {
                        set_pet_id(v);
                        const p = pets.find((x) => x.id === v);
                        if (p) {
                          set_deceased_name(p.name);
                          set_deceased_species(p.species);
                        }
                      }}
                      options={[
                        { value: '', label: 'Enter details manually' },
                        ...pets.map((p) => ({ value: p.id, label: p.name })),
                      ]}
                    />
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="deceased-name">Pet’s name</Label>
                    <Input id="deceased-name" value={deceased_name} onChange={(e) => set_deceased_name(e.target.value)} placeholder="Bruno" data-testid="deceased-name" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="deceased-species">Species</Label>
                    <SelectField
                      id="deceased-species"
                      value={deceased_species}
                      data-testid="deceased-species"
                      placeholder="Choose species"
                      onValueChange={(v) => set_deceased_species(v as PetSpecies)}
                      options={SPECIES_OPTIONS}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="deceased-weight" className="flex items-center gap-1">
                      Approx. weight (kg){' '}
                      <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <Input id="deceased-weight" type="number" min={0} value={deceased_weight} onChange={(e) => set_deceased_weight(e.target.value)} placeholder="12" data-testid="deceased-weight" />
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="cremation-date">Preferred pickup date</Label>
                    <DatePicker id="cremation-date" min={today_ist()} value={date} onChange={set_date} data-testid="cremation-date" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="cremation-time">Preferred time</Label>
                    <SelectField
                      id="cremation-time"
                      value={pickup_time}
                      data-testid="cremation-time"
                      onValueChange={set_pickup_time}
                      options={PICKUP_TIME_OPTIONS}
                    />
                  </div>
                </div>
              </>
            )}
            {is_lab && service?.lab_fasting_required && (
              <div
                className="flex items-start gap-2 rounded-input border-2 border-warning/30 bg-warning/5 p-3 text-sm"
                data-testid="lab-fasting-reminder"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <span>
                  This test needs fasting — please keep your pet off food (water is fine) before the sample is collected.
                </span>
              </div>
            )}
            {is_cremation ? null : is_event ? (
              <div className="flex items-center gap-2 rounded-input border-2 border-primary/25 bg-primary/5 p-3 text-sm font-medium" data-testid="event-when">
                <CalendarClock className="h-4 w-4 text-primary" />
                {slots[0] ? event_label(slots[0].slot_start) : 'Event time to be announced'}
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="book-date">Date</Label>
                  <DatePicker
                    id="book-date"
                    min={today_ist()}
                    value={date}
                    onChange={(v) => {
                      set_date(v);
                      set_slot_start(null);
                    }}
                    className="w-56"
                    data-testid="book-date"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label>{is_dining ? 'Available times' : 'Available times'}</Label>
                  {active_q.isLoading ? (
                    <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                  ) : active_q.error ? (
                    <p className="text-sm text-destructive" data-testid="slots-error">
                      {form_error_message(active_q.error, 'Could not load times.')}
                    </p>
                  ) : slots.length === 0 ? (
                    <div className="flex items-center gap-2 rounded-input border-2 border-brand-outline-variant/15 bg-secondary/40 p-3 text-sm text-muted-foreground" data-testid="slots-empty">
                      <CalendarDays className="h-4 w-4" />
                      {date_beyond_horizon
                        ? `This provider only takes bookings up to ${horizon_days} days ahead. Pick an earlier date.`
                        : 'No open times on this date. Try another day.'}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="slots-grid">
                      {slots.map((s) => (
                        <button
                          key={s.slot_start}
                          type="button"
                          onClick={() => set_slot_start(s.slot_start)}
                          data-testid={`slot-${s.slot_start}`}
                          className={`rounded-button border-2 px-2 py-2.5 text-sm font-semibold transition-all duration-fast ease-bounce active:scale-95 ${
                            slot_start === s.slot_start
                              ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
                              : 'border-input hover:border-primary/50 hover:bg-muted'
                          }`}
                        >
                          {slot_label(s.slot_start)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {is_cremation ? null : needs_pet && pets.length === 0 ? (
              <div className="flex items-center justify-between gap-3 rounded-input border-2 border-warning/30 bg-warning/5 p-3 text-sm" data-testid="walk-needs-pet">
                <span>Add your {pet_noun} before booking a {visit_noun}.</span>
                <Button size="sm" variant="outline" onClick={() => navigate('/app/pets/new')} data-testid="walk-add-pet">
                  Add a {pet_noun}
                </Button>
              </div>
            ) : (
              pets.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="book-pet">{needs_pet ? (is_walk ? 'Dog' : 'Pet') : 'Pet (optional)'}</Label>
                  <SelectField
                    id="book-pet"
                    value={pet_id}
                    onValueChange={set_pet_id}
                    data-testid="book-pet"
                    options={
                      needs_pet
                        ? [{ value: '', label: `Select your ${pet_noun}` }, ...pets.map((p) => ({ value: p.id, label: p.name }))]
                        : [{ value: '', label: 'No specific pet' }, ...pets.map((p) => ({ value: p.id, label: p.name }))]
                    }
                  />
                </div>
              )
            )}

            {/* Visit mode chooser (client change, 9 Oct 2026) — only when there's a real choice. */}
            {offered_modes.length > 1 && service && (
              <div className="flex flex-col gap-1.5" data-testid="visit-mode-chooser">
                <Label>How would you like it?</Label>
                <div className="flex flex-wrap gap-2">
                  {VISIT_MODE_OPTIONS[is_lab ? 'LAB' : is_groomer ? 'GROOMER' : 'CREMATION']
                    .filter((o) => offered_modes.includes(o.value))
                    .map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => set_visit_mode(o.value)}
                        data-testid={`visit-mode-${o.value}`}
                        data-active={chosen_mode === o.value}
                        className={
                          'rounded-button border-2 px-4 py-2 text-sm font-semibold transition-all duration-fast ' +
                          (chosen_mode === o.value ? 'border-primary text-primary shadow-clay-sm' : 'border-input bg-card text-muted-foreground')
                        }
                      >
                        {o.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Pickup location — where the walker collects the dog, the trainer visits, or the provider collects a pet for cremation. */}
            {needs_pickup && (
              <div className="flex flex-col gap-3 rounded-input border-2 border-brand-outline-variant/15 bg-secondary/30 p-3" data-testid="walk-pickup-fields">
                <p className="text-sm font-semibold">{is_walk ? 'Pickup details' : is_cremation ? 'Home pickup details' : 'Visit details'}</p>
                <SavedAddressPicker
                  on_select={(a) => {
                    set_saved_address_id(a.id);
                    set_pickup_address([a.address, a.city].filter(Boolean).join(', '));
                    set_pickup_landmark(a.landmark ?? '');
                    set_pickup_pincode(a.pincode ?? '');
                    if (a.phone) set_contact_phone(a.phone);
                    if (a.access_notes) set_access_notes(a.access_notes);
                  }}
                  on_clear={() => {
                    set_saved_address_id(undefined);
                    set_pickup_address('');
                    set_pickup_landmark('');
                    set_pickup_pincode('');
                  }}
                />
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pickup-address">Address</Label>
                  <Input
                    id="pickup-address"
                    value={pickup_address}
                    onChange={(e) => {
                      set_saved_address_id(undefined);
                      set_pickup_address(e.target.value);
                    }}
                    placeholder="House / building, street, area"
                    data-testid="pickup-address"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="pickup-landmark">Landmark (optional)</Label>
                    <Input id="pickup-landmark" value={pickup_landmark} onChange={(e) => set_pickup_landmark(e.target.value)} placeholder="Near…" data-testid="pickup-landmark" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="pickup-pincode">Pincode (optional)</Label>
                    <Input id="pickup-pincode" value={pickup_pincode} onChange={(e) => set_pickup_pincode(e.target.value)} placeholder="560001" data-testid="pickup-pincode" />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="contact-phone">Contact phone</Label>
                  <Input id="contact-phone" value={contact_phone} onChange={(e) => set_contact_phone(e.target.value)} placeholder="+91…" data-testid="contact-phone" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="access-notes">Access notes (optional)</Label>
                  <Input id="access-notes" value={access_notes} onChange={(e) => set_access_notes(e.target.value)} placeholder="Gate code, floor, where to meet…" data-testid="access-notes" />
                </div>
              </div>
            )}

            {is_dining && (
              <div className="flex flex-col gap-1.5">
                <Label>{is_event ? 'Tickets' : 'Party size'}</Label>
                <div className="flex items-center gap-3" data-testid="party-size">
                  <button
                    type="button"
                    onClick={() => set_party_size((n) => Math.max(offering!.min_party, n - 1))}
                    aria-label="Fewer"
                    className="rounded-full bg-secondary p-2 active:scale-90 disabled:opacity-40"
                    disabled={party_size <= offering!.min_party}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="flex items-center gap-1.5 text-lg font-bold tabular-nums">
                    <Users className="h-4 w-4 text-muted-foreground" /> {party_size}
                  </span>
                  <button
                    type="button"
                    onClick={() => set_party_size((n) => Math.min(max_party, n + 1))}
                    aria-label="More"
                    className="rounded-full bg-primary/10 p-2 text-primary active:scale-90 disabled:opacity-40"
                    disabled={party_size >= max_party}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="ml-auto font-heading text-base font-bold tabular-nums" data-testid="dining-total">
                    ₹{dining_total}
                  </span>
                </div>
                {selected_slot?.remaining != null && (
                  <span className="text-xs text-muted-foreground">{selected_slot.remaining} spaces left for this time.</span>
                )}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="book-notes">Notes (optional)</Label>
              <textarea
                id="book-notes"
                value={notes}
                onChange={(e) => set_notes(e.target.value)}
                rows={2}
                className="rounded-input border-2 border-input bg-card px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15"
                placeholder="Anything the venue should know…"
                data-testid="book-notes"
              />
            </div>

            {region_block && <RegionBlockCallout error={region_block} />}

            {error && (
              <p className="text-sm text-destructive" data-testid="book-error">
                {error}
              </p>
            )}

            <Button onClick={reserve} disabled={(!is_cremation && !slot_start) || create_m.isPending} variant="gradient" data-testid="book-reserve">
              {create_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Continue to payment
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — payment */}
      {step === 'pay' && booking && (
        <Card data-testid="payment-screen">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" /> Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 p-5 pt-0">
            <div className="rounded-input border-2 border-brand-outline-variant/15 bg-secondary/40 p-3 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">{offering?.name ?? service?.name ?? 'Booking'}</span>
                <span className="font-semibold tabular-nums">₹{amount}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">When</span>
                <span>{new Date(booking.slot_start).toLocaleString('en-IN', { timeZone: IST, dateStyle: 'medium', timeStyle: 'short' })}</span>
              </div>
              {booking.party_size != null && (
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">{is_event ? 'Tickets' : 'Party'}</span>
                  <span>{booking.party_size}</span>
                </div>
              )}
            </div>
            <p className="rounded-input border-2 border-dashed border-brand-outline-variant/30 p-2 text-center text-xs text-muted-foreground">
              {checkout ? 'Secure payment via Razorpay.' : 'Demo gateway — no real charge. This always succeeds.'}
            </p>

            {error && (
              <p className="text-sm text-destructive" data-testid="pay-error">
                {error}
              </p>
            )}

            <Button onClick={checkout ? pay_razorpay : pay_dummy} disabled={paying} variant="gradient" data-testid="pay-now">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Pay ₹{amount}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — confirmation */}
      {step === 'done' && booking && (
        <Card data-testid="booking-confirmation">
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-success" />
            <h2 className="font-heading text-xl font-bold">{is_dining ? 'Booking requested' : 'Booking requested'}</h2>
            <p className="text-sm text-muted-foreground">
              {booking.payment
                ? "Payment captured. We've sent your request to the venue — you'll be notified when they confirm."
                : "We've sent your request to the venue — you'll be notified when they confirm."}
            </p>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/app/bookings')} variant="gradient" data-testid="go-to-bookings">
                View my bookings
              </Button>
              <Button onClick={() => navigate('/app/discover')} variant="ghost">
                Keep browsing
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
